/**
 * Mech assets cached per chassis: the loaded template scene (never added to the world), its
 * clips, its sidecar (LOD nodes, sockets, livery defaults) and the texture slots the livery
 * material takes over. Views clone the template, so two mechs on one chassis share geometry and
 * textures. The decal atlas is loaded once per cache and shared by every view.
 */
import { DecalAtlasSchema, MechSidecarSchema, type MechSidecar } from '@steelwake/shared/data';
import {
  MeshStandardMaterial,
  type AnimationClip,
  type Group,
  type Mesh,
  type Object3D,
  type SkinnedMesh,
} from 'three/webgpu';

import { disposeObject, setAnisotropy } from '../assets/dispose.js';
import type { Loaders } from '../assets/loaders.js';
import { assetUrl } from '../assets/paths.js';
import { liveryMapsOf, type DecalAtlas, type LiveryMaps } from './livery.js';

export interface MechAsset {
  chassis: string;
  template: Group;
  clips: Map<string, AnimationClip>;
  sidecar: MechSidecar;
  /** Body-only height (feet to crown) from the sidecar, metres. */
  height: number;
  maps: { body: LiveryMaps; kit: LiveryMaps };
  atlas: DecalAtlas;
}

/** Every mesh of a mech: the skinned body and kit per LOD plus the rigid weapons and cosmetics. */
export function meshesOf(root: Object3D): Mesh[] {
  const meshes: Mesh[] = [];
  root.traverse((o: Object3D) => {
    if ((o as Mesh).isMesh) meshes.push(o as Mesh);
  });
  return meshes;
}

/** The body meshes carry the "body" material; everything else is kit (parts, weapons, cosmetics). */
export function isBodyMesh(mesh: Mesh): boolean {
  const material = mesh.material;
  return !Array.isArray(material) && material.name === 'body';
}

/** Skinned meshes per LOD level, in the sidecar's order. */
export function lodGroupsOf(root: Object3D, sidecar: MechSidecar): SkinnedMesh[][] {
  const byName = new Map<string, Object3D>();
  root.traverse((o: Object3D) => byName.set(o.name, o));
  return sidecar.lods.map((lod) =>
    lod.nodes.map((name) => {
      const node = byName.get(name);
      if (!node || !(node as SkinnedMesh).isSkinnedMesh) {
        throw new Error(`mech ${sidecar.mech}: LOD node "${name}" is not a skinned mesh`);
      }
      return node as SkinnedMesh;
    }),
  );
}

async function fetchJson(url: string, signal: AbortSignal): Promise<unknown> {
  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error(`${url}: HTTP ${String(response.status)}`);
  return (await response.json()) as unknown;
}

export class MechAssetCache {
  private readonly pending = new Map<string, Promise<MechAsset>>();
  private readonly loaded: MechAsset[] = [];
  private atlasPending: Promise<DecalAtlas> | null = null;
  private atlasLoaded: DecalAtlas | null = null;

  constructor(
    private readonly loaders: Loaders,
    private readonly anisotropy: number,
  ) {}

  load(
    chassis: string,
    signal: AbortSignal,
    onProgress?: (event: ProgressEvent) => void,
  ): Promise<MechAsset> {
    let p = this.pending.get(chassis);
    if (!p) {
      p = this.fetch(chassis, signal, onProgress);
      this.pending.set(chassis, p);
    }
    return p;
  }

  setAnisotropy(value: number): void {
    for (const asset of this.loaded) setAnisotropy(asset.template, value);
  }

  dispose(): void {
    for (const asset of this.loaded) disposeObject(asset.template);
    this.loaded.length = 0;
    this.pending.clear();
    this.atlasLoaded?.texture.dispose();
    this.atlasLoaded = null;
    this.atlasPending = null;
  }

  /** The stencil atlas and its tile order; loaded once and shared by every mech. */
  atlas(signal: AbortSignal): Promise<DecalAtlas> {
    this.atlasPending ??= (async () => {
      const [texture, json] = await Promise.all([
        this.loaders.ktx2.loadAsync(assetUrl('decals/atlas.ktx2')),
        fetchJson(assetUrl('decals/atlas.json'), signal),
      ]);
      const meta = DecalAtlasSchema.parse(json);
      texture.anisotropy = this.anisotropy;
      const atlas: DecalAtlas = { texture, columns: meta.columns, tiles: meta.tiles };
      this.atlasLoaded = atlas;
      return atlas;
    })();
    return this.atlasPending;
  }

  private async fetch(
    chassis: string,
    signal: AbortSignal,
    onProgress?: (event: ProgressEvent) => void,
  ): Promise<MechAsset> {
    const [gltf, sidecarJson, atlas] = await Promise.all([
      this.loaders.gltf.loadAsync(assetUrl(`mechs/${chassis}/mech.glb`), onProgress),
      fetchJson(assetUrl(`mechs/${chassis}/mech.json`), signal),
      this.atlas(signal),
    ]);
    if (signal.aborted) {
      disposeObject(gltf.scene);
      signal.throwIfAborted();
    }
    const sidecar = MechSidecarSchema.parse(sidecarJson);
    const meshes = meshesOf(gltf.scene);
    const materials = new Map<string, MeshStandardMaterial>();
    for (const mesh of meshes) {
      // Skinned bounds do not follow the animation; never let the frustum cull the mech.
      mesh.frustumCulled = false;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      const material = mesh.material;
      if (!Array.isArray(material) && material instanceof MeshStandardMaterial) {
        materials.set(material.name, material);
      }
    }
    const body = materials.get('body');
    const kit = materials.get('kit');
    if (!body || !kit) {
      const found = [...materials.keys()].join(', ') || 'none';
      throw new Error(`mech ${chassis}: expected "body" and "kit" materials, found ${found}`);
    }
    lodGroupsOf(gltf.scene, sidecar); // throws on a sidecar/model mismatch before anything renders
    setAnisotropy(gltf.scene, this.anisotropy);
    const asset: MechAsset = {
      chassis,
      template: gltf.scene,
      clips: new Map(gltf.animations.map((c) => [c.name, c])),
      sidecar,
      height: sidecar.heightMetres,
      maps: { body: liveryMapsOf(body), kit: liveryMapsOf(kit) },
      atlas,
    };
    this.loaded.push(asset);
    return asset;
  }
}
